// Full session tracking — one growing row per visit in public.session_track.
//
// What we capture:
//   - keystrokes (every key down, except in <input type="password"> or [data-private])
//   - mouse clicks (with target tag, id, class, text, link href if any)
//   - scroll (throttled to 1/sec)
//   - page focus (open / close timestamps per pageId)
//   - identity from ipapi.co + navigator + screen
//
// All events go into a JSON array that we UPSERT every 5 seconds.
// On beforeunload we flush via navigator.sendBeacon to survive tab close.

import { getSupabase } from './SupabaseClient';
import { getCachedGeo } from './Tracker';

const SESSION_KEY = 'gb4k_session_id_v1';
const UPSERT_INTERVAL_MS = 5000;
const SCROLL_THROTTLE_MS = 1000;
const KEYSTROKE_BUFFER_MAX = 5000; // safety: hard cap on stored events

type EventBase = { t: number };
export type TrackEvent =
  | (EventBase & { kind: 'key'; key: string; mods: string; targetTag?: string; targetId?: string })
  | (EventBase & { kind: 'click'; tag: string; id?: string; cls?: string; text?: string; href?: string })
  | (EventBase & { kind: 'scroll'; y: number; pct: number })
  | (EventBase & { kind: 'page_open'; page: string })
  | (EventBase & { kind: 'page_close'; page: string; durMs: number })
  | (EventBase & { kind: 'cmd'; cmd: string })           // explicit command executed
  | (EventBase & { kind: 'focus' })
  | (EventBase & { kind: 'blur' });

interface SessionState {
  sessionId: string;
  startedAt: number;
  events: TrackEvent[];
  pageCount: number;
  keystrokeCount: number;
  clickCount: number;
  identityWritten: boolean;
  currentPage: { id: string; openedAt: number } | null;
  lastScrollAt: number;
}

let state: SessionState | null = null;
let upsertTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;

function nowOffset(): number {
  return state ? Math.round(performance.now()) : 0;
}

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-session-' + Date.now();
  }
}

function buildIdentityPayload() {
  const geo = getCachedGeo() ?? {};
  return {
    ip: geo.ip ?? null,
    country: geo.country ?? null,
    country_code: geo.country_code ?? null,
    city: geo.city ?? null,
    region: geo.region ?? null,
    latitude: geo.latitude ?? null,
    longitude: geo.longitude ?? null,
    timezone: geo.timezone ?? null,
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
  };
}

function isPrivateTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof Element)) return false;
  if (el instanceof HTMLInputElement && el.type === 'password') return true;
  // Allow opt-out via data attribute on the input or any ancestor
  let cur: Element | null = el;
  while (cur) {
    if (cur.getAttribute('data-private') === 'true') return true;
    cur = cur.parentElement;
  }
  return false;
}

function describeClickTarget(el: EventTarget | null): TrackEvent {
  const base = { kind: 'click' as const, t: nowOffset(), tag: 'unknown' };
  if (!(el instanceof Element)) return base;
  const tag = el.tagName.toLowerCase();
  const id = el.id || undefined;
  const cls = el.className && typeof el.className === 'string' ? el.className.slice(0, 80) : undefined;
  const text = (el.textContent || '').trim().slice(0, 60) || undefined;
  // walk up to find an <a href> if the click landed on a child
  let cur: Element | null = el;
  let href: string | undefined;
  while (cur) {
    if (cur instanceof HTMLAnchorElement && cur.href) { href = cur.href; break; }
    cur = cur.parentElement;
  }
  return { kind: 'click', t: nowOffset(), tag, id, cls, text, href };
}

function pushEvent(ev: TrackEvent) {
  if (!state) return;
  if (state.events.length >= KEYSTROKE_BUFFER_MAX) return; // drop silently past the cap
  state.events.push(ev);
}

// ===== Public API =====

export function initSessionTracker(): void {
  if (initialized) return;
  initialized = true;

  const supa = getSupabase();
  if (!supa) {
    console.warn('[SessionTracker] Supabase missing — tracking disabled.');
    return;
  }

  state = {
    sessionId: getOrCreateSessionId(),
    startedAt: Date.now(),
    events: [],
    pageCount: 0,
    keystrokeCount: 0,
    clickCount: 0,
    identityWritten: false,
    currentPage: null,
    lastScrollAt: 0,
  };

  // Listeners
  window.addEventListener('keydown', onKeydown, true);
  window.addEventListener('click', onClick, true);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('focus', () => pushEvent({ kind: 'focus', t: nowOffset() }));
  window.addEventListener('blur', () => pushEvent({ kind: 'blur', t: nowOffset() }));
  window.addEventListener('beforeunload', flushFinal);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushFinal();
  });

  // Periodic upsert
  upsertTimer = setInterval(() => { upsert().catch(() => {}); }, UPSERT_INTERVAL_MS);

  // First upsert (writes identity) — wait a moment for geo to resolve.
  setTimeout(() => { upsert().catch(() => {}); }, 1200);
}

export function recordPageOpen(pageId: string): void {
  if (!state) return;
  // Close previous page (if any) first
  if (state.currentPage) {
    const dur = Date.now() - state.currentPage.openedAt;
    pushEvent({ kind: 'page_close', t: nowOffset(), page: state.currentPage.id, durMs: dur });
  }
  state.pageCount += 1;
  state.currentPage = { id: pageId, openedAt: Date.now() };
  pushEvent({ kind: 'page_open', t: nowOffset(), page: pageId });
}

export function recordPageClose(): void {
  if (!state || !state.currentPage) return;
  const dur = Date.now() - state.currentPage.openedAt;
  pushEvent({ kind: 'page_close', t: nowOffset(), page: state.currentPage.id, durMs: dur });
  state.currentPage = null;
}

export function recordCommand(cmd: string): void {
  if (!state) return;
  pushEvent({ kind: 'cmd', t: nowOffset(), cmd: cmd.slice(0, 200) });
}

// ===== Listeners =====

function onKeydown(e: KeyboardEvent) {
  if (!state) return;
  if (isPrivateTarget(e.target)) return;
  state.keystrokeCount += 1;
  const target = e.target instanceof Element ? e.target : null;
  const mods =
    (e.ctrlKey ? 'C' : '') +
    (e.metaKey ? 'M' : '') +
    (e.shiftKey ? 'S' : '') +
    (e.altKey ? 'A' : '');
  pushEvent({
    kind: 'key',
    t: nowOffset(),
    key: e.key,
    mods,
    targetTag: target?.tagName.toLowerCase(),
    targetId: target?.id || undefined,
  });
}

function onClick(e: MouseEvent) {
  if (!state) return;
  state.clickCount += 1;
  pushEvent(describeClickTarget(e.target));
}

function onScroll() {
  if (!state) return;
  const now = performance.now();
  if (now - state.lastScrollAt < SCROLL_THROTTLE_MS) return;
  state.lastScrollAt = now;
  const y = window.scrollY;
  const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  pushEvent({ kind: 'scroll', t: nowOffset(), y, pct: Math.round((y / max) * 100) });
}

// ===== Upsert =====

let lastUpsertEventCount = 0;

async function upsert(): Promise<void> {
  const supa = getSupabase();
  if (!supa || !state) return;

  // Skip if no new events since last upsert (and identity already written)
  if (state.identityWritten && state.events.length === lastUpsertEventCount) return;
  lastUpsertEventCount = state.events.length;

  const row: any = {
    session_id: state.sessionId,
    updated_at: new Date().toISOString(),
    events: state.events,
    page_count: state.pageCount,
    keystroke_count: state.keystrokeCount,
    click_count: state.clickCount,
  };
  if (!state.identityWritten) {
    Object.assign(row, buildIdentityPayload());
  }

  const { error } = await supa
    .from('gb4k_fr_session_track')
    .upsert(row, { onConflict: 'session_id' });

  if (error) {
    console.warn('[SessionTracker] upsert failed:', error.message);
  } else {
    state.identityWritten = true;
  }
}

function flushFinal(): void {
  if (!state) return;
  // Close any open page
  if (state.currentPage) {
    const dur = Date.now() - state.currentPage.openedAt;
    state.events.push({ kind: 'page_close', t: nowOffset(), page: state.currentPage.id, durMs: dur });
    state.currentPage = null;
  }

  const url = (import.meta.env.VITE_SUPABASE_URL as string) + '/rest/v1/gb4k_fr_session_track?on_conflict=session_id';
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  if (!url || !apikey) return;

  const row: any = {
    session_id: state.sessionId,
    updated_at: new Date().toISOString(),
    ended_at: new Date().toISOString(),
    events: state.events,
    page_count: state.pageCount,
    keystroke_count: state.keystrokeCount,
    click_count: state.clickCount,
  };
  if (!state.identityWritten) Object.assign(row, buildIdentityPayload());

  // sendBeacon needs a Blob with proper headers — we use fetch with keepalive
  // as a more compatible fallback since sendBeacon can't set custom headers
  // (Supabase needs the apikey / Prefer headers).
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': apikey,
        'Authorization': `Bearer ${apikey}`,
        'Prefer': 'resolution=merge-duplicates',
      },
      body: JSON.stringify(row),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

export function stopSessionTracker(): void {
  if (upsertTimer) {
    clearInterval(upsertTimer);
    upsertTimer = null;
  }
  initialized = false;
}
